import { Relationship, Resource } from 'json-api';
import { AtomResource } from '../classes/atom-resource';
import { BillingChannel } from './billing-channel.resource';
import { BillingKinematic } from './billing-kinematic.resource';
import { Cascade } from './cascade.resource';
import { Catalog } from './catalog.resource';
import { Contract } from './contract.resource';
import { Country } from './country.resource';
import { Job } from './job.resource';
import { NetworkChannel } from './network-channel.resource';
import { OrderChannel } from './order-channel.resource';
import { Platform } from './platform.resource';
import { SKU } from './sku.resource';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import {
  CountryConfig,
  BillingChannelConfig,
  BillingKinematicConfig,
  CascadeConfig,
  FirmJobConfig,
  NetworkChannelConfig,
  OrderChannelConfig,
  PlatformConfig,
} from 'src/app/shared/tools/search/search-config';
import { AtomService } from '../atom.service';

@Resource({
  type: 'paymentdimensions',
})
export class PaymentDimension extends AtomResource {
  @Relationship() paymentdimensionsource?: PaymentDimension;
  @Relationship() childpaymentdimensions?: PaymentDimension[];
  @Relationship() billingchannel?: BillingChannel;
  @Relationship() billingkinematic?: BillingKinematic;
  @Relationship() billingproviderjob?: Job;
  @Relationship() cascade?: Cascade;
  @Relationship() country?: Country;
  @Relationship() enduserproviderjob?: Job;
  @Relationship() networkchannel?: NetworkChannel;
  @Relationship() orderchannel?: OrderChannel;
  @Relationship() payoutproviderjob?: Job;
  @Relationship() platform?: Platform;
  @Relationship() contract?: Contract;
  @Relationship() catalog?: Catalog;
  @Relationship() sku?: SKU;
}

export class PaymentDimensionConfig implements ResourceConfig {
  tables = {
    options: {
      copy: true,
    },
    includes: [
      'country',
      'billingchannel',
      'enduserproviderjob.firm.headquarterscountry',
      'enduserproviderjob.activity',
      'billingproviderjob.firm.headquarterscountry',
      'payoutproviderjob.firm.headquarterscountry',
      'billingkinematic',
      'orderchannel',
      'networkchannel',
      'platform',
      'cascade',
      'paymentdimensionsource.cascade',
      'contract',
      'catalog',
    ],
    columns: [
      {
        columnDef: 'inherited',
        displayedDatas: [
          {
            path: [''],
            type: 'overwrite',
          },
        ],
        header: 'Inherited',
        displayed: true,
      },
      {
        columnDef: 'enduserproviderjob-country',
        displayedDatas: [
          {
            path: ['enduserproviderjob.firm.name'],
            class: 'mat-body-2',
          },
          {
            path: ['country.id'],
            class: 'mat-body-2',
          },
        ],
        header: 'End User Provider',
        displayed: true,
        options: {
          flag: 'country.id',
        },
      },
      {
        columnDef: 'billingpayoutprovider',
        displayedDatas: [
          {
            path: ['billingproviderjob.firm.name'],
            class: 'mat-body-2',
          },
          {
            path: ['platform.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Billing / Platform',
        displayed: true,
      },
      {
        columnDef: 'billingchannel',
        displayedDatas: [
          {
            path: ['billingchannel.name'],
            class: 'mat-body-2',
          },
          {
            path: ['billingkinematic.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Billing',
        displayed: true,
      },
      {
        columnDef: 'orderchannel',
        displayedDatas: [
          {
            path: ['orderchannel.name'],
            class: 'mat-body-2',
          },
          {
            path: ['networkchannel.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Order Channel',
        displayed: true,
      },
      {
        columnDef: 'cascade',
        displayedDatas: [
          {
            path: ['paymentdimensionsource.cascade.name'],
            class: 'stroke',
            nullIfEmpty: true,
          },
          {
            path: ['cascade.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Cascade',
        displayed: true,
        options: {
          overloadable: {
            create: PaymentDimension,
            type: 'resource',
            formControl: 'cascade',
            APIKey: 'cascade',
            search: new CascadeConfig(this.atom),
            parent: 'paymentdimensionsource',
            inheritDatas: [{ key: 'catalog' }],
            displayedDatas: [
              {
                path: ['cascade.name'],
                class: 'mat-body-2',
              },
            ],
          },
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
        options: {
          copy: true,
        },
      },
    ],
  };
  detail = {
    header: {
      icon: 'business',
      title: [
        {
          path: ['enduserproviderjob.firm.name'],
          translatable: false,
        },
      ],
      subtitle: [
        {
          path: ['country.id', 'billingchannel.name', 'billingkinematic.name'],
          translatable: false,
        },
      ],
      options: {
        flag: 'country.id',
      },
    },
    elements: [
      {
        showEmpty: true,
        list: [
          {
            icon: 'business',
            title: 'Billing Provider',
            APIKey: 'billingproviderjob',
            value: {
              path: ['billingproviderjob.firm.name'],
            },
            link: {
              resource: 'firms',
              filter: 'billingproviderjob.firm.id',
            },
            options: {
              flag: 'billingproviderjob.firm.headquarterscountry.id',
            },
          },
          {
            icon: 'business',
            title: 'Payout Provider',
            APIKey: 'payoutproviderjob',
            value: {
              path: ['payoutproviderjob.firm.name'],
            },
            link: {
              resource: 'firms',
              filter: 'payoutproviderjob.firm.id',
            },
            options: {
              flag: 'payoutproviderjob.firm.headquarterscountry.id',
            },
          },
          {
            icon: 'business',
            title: 'Enduser Provider',
            APIKey: 'enduserproviderjob',
            value: {
              path: [
                'enduserproviderjob.firm.name',
                'enduserproviderjob.activity.name',
              ],
            },
            link: {
              resource: 'firms',
              filter: 'enduserproviderjob.firm.id',
            },
            options: {
              flag: 'enduserproviderjob.firm.headquarterscountry.id',
            },
          },
          {
            icon: 'payment',
            title: 'Billing Channel',
            APIKey: 'billingchannel',
            value: {
              path: ['billingchannel.name'],
            },
          },
          {
            icon: 'payment',
            title: 'Billing Kinematic',
            APIKey: 'billingkinematic',
            value: {
              path: ['billingkinematic.name'],
            },
          },
          {
            icon: 'wifi',
            title: 'Network Channel',
            APIKey: 'networkchannel',
            value: {
              path: ['networkchannel.name'],
            },
          },
          {
            icon: 'share',
            title: 'Order Channel',
            APIKey: 'orderchannel',
            value: {
              path: ['orderchannel.name'],
            },
          },
          {
            icon: 'dns',
            title: 'Platform',
            APIKey: 'platform',
            value: {
              path: ['platform.name'],
            },
            link: {
              resource: 'platforms',
              filter: 'platform.id',
            },
          },
          {
            icon: 'shopping_cart',
            title: 'Cascade',
            APIKey: 'cascade',
            value: {
              path: ['cascade.name'],
            },
            link: {
              resource: 'cascades',
              filter: 'cascade.id',
            },
          },
        ],
      },
    ],
  };
  wizard = {
    tabs: [
      {
        title: 'Providers',
        formGroup: 'general',
        list: [
          {
            type: 'resource',
            title: 'Country',
            formControl: 'country',
            APIKey: 'country',
            search: new CountryConfig(this.atom),
            noCols: true,
            required: true,
            filter: [
              {
                key: 'equal.businessunits.id',
                value: 'id',
                multiple: 'contract.businessunits'
              },
            ],
            activatingSequence: [
              {
                group: 'cascade',
                control: 'cascade',
                filters: {
                  filter: 'like.country.id',
                },
              },
              {
                group: 'general',
                control: 'enduserproviderjob',
                filters: {
                  filter: 'like.firm.headquarterscountry.id',
                },
              },
              {
                group: 'general',
                control: 'payoutproviderjob',
                filters: {
                  filter: 'like.firm.headquarterscountry.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Billing Channel',
            formControl: 'billingchannel',
            APIKey: 'billingchannel',
            search: new BillingChannelConfig(),
            noCols: true,
            required: true,
            activatingSequence: [
              {
                group: 'general',
                control: 'enduserproviderjob',
                routing: {
                  '1': '2',
                  '2': '2',
                  '3': '2',
                  '4': '3',
                  '5': '4',
                  '6': '4',
                  '7': '4',
                  filter: 'equal.activity.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Enduser Provider',
            formControl: 'enduserproviderjob',
            APIKey: 'enduserproviderjob',
            search: new FirmJobConfig(this.atom),
            required: true,
            activatingSequence: [
              {
                group: 'general',
                control: 'platform',
                filters: {
                  filter: 'equal.platformcapabilities.enduserproviderjob.id',
                },
              },
              {
                group: 'general',
                control: 'billingproviderjob',
                filters: {
                  filter:
                    'equal.platforms.platformcapabilities.enduserproviderjob.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Billing Provider',
            formControl: 'billingproviderjob',
            APIKey: 'billingproviderjob',
            search: new FirmJobConfig(this.atom),
            required: true,
            filter: [
              {
                key: 'equal.activity.id',
                fixValue: '1',
              },
            ],
            activatingSequence: [
              {
                group: 'general',
                control: 'platform',
                filters: {
                  filter: 'equal.job.id',
                },
              },
              {
                group: 'general',
                control: 'payoutproviderjob',
                filters: {
                  filter: 'like.firm.headquarterscountry.id',
                  value: 'firm.headquarterscountry.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Payout Provider Firm',
            formControl: 'payoutproviderjob',
            APIKey: 'payoutproviderjob',
            search: new FirmJobConfig(this.atom),
            required: true,
            filter: [
              {
                key: 'equal.activity.id',
                fixValue: '5',
              },
            ],
          },
          {
            type: 'resource',
            title: 'Platform',
            formControl: 'platform',
            APIKey: 'platform',
            search: new PlatformConfig(this.atom),
            required: true,
            activatingSequence: [
              {
                group: 'customfields',
                control: 'customfields',
                type: 'resource',
                host: true,
              },
            ],
          },
        ],
      },
      {
        title: 'Dimensions',
        formGroup: 'dimensions',
        list: [
          {
            type: 'resource',
            title: 'Billing Kinematic',
            formControl: 'billingkinematic',
            APIKey: 'billingkinematic',
            search: new BillingKinematicConfig(),
            required: true,
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Network Channel',
            formControl: 'networkchannel',
            APIKey: 'networkchannel',
            search: new NetworkChannelConfig(),
            required: true,
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Order Channel',
            formControl: 'orderchannel',
            APIKey: 'orderchannel',
            search: new OrderChannelConfig(),
            required: true,
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Cascade',
            formControl: 'cascade',
            APIKey: 'cascade',
            search: new CascadeConfig(this.atom),
            noCols: true,
          },
        ],
      },
      {
        title: 'Custom Fields',
        formGroup: 'customfields',
        list: [
          {
            type: 'customfields',
            formControl: 'customfields',
            APIKey: 'data',
            noCols: true,
          },
        ],
      },
    ],
  };
  resource = PaymentDimension;
  resourceKey?: string | undefined = 'paymentdimensions';
  resourceKeySingle?: string | undefined = 'paymentdimension';
  resourceName?: string | undefined = 'paymentdimensions';
  includes = [
    'country',
    'billingchannel',
    'enduserproviderjob.firm.headquarterscountry',
    'enduserproviderjob.activity',
    'billingproviderjob.firm.headquarterscountry',
    'billingproviderjob.activity',
    'payoutproviderjob.firm.headquarterscountry',
    'payoutproviderjob.activity',
    'billingkinematic',
    'orderchannel',
    'networkchannel',
    'platform.job.activity',
    'cascade',
    'contract.businessunits',
    'catalog',
    'sku',
  ];
  constructor(
    private atom: AtomService,
    public hosted: string = 'contract',
    public unoverloadable: boolean = false
  ) {}
}
